import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../lib/admin-api";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useI18n } from "../../context/I18nContext";

export default function AdminLogin() {
  const { user, refresh } = useAdminAuth();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // If already authenticated, jump to dashboard
  useEffect(() => {
    if (user) navigate("/admin/dashboard", { replace: true });
  }, [user, navigate]);

  const formatError = (detail) => {
    if (detail == null) return lang === "nl" ? "Er ging iets mis." : "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (typeof detail?.msg === "string") return detail.msg;
    return String(detail);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await adminApi.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });
      await refresh();
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || (lang === "nl" ? "Inloggen mislukt." : "Sign-in failed."));
    } finally {
      setBusy(false);
    }
  };

  const labels = lang === "nl"
    ? { eyebrow: "— Admin", subtitle: "Toegang is voorbehouden aan ingelogde redacteurs van DeStoel.com.", email: "E-mailadres", password: "Wachtwoord", submit: "Inloggen", busy: "Bezig met inloggen…" }
    : { eyebrow: "— Admin", subtitle: "Access is restricted to logged-in DeStoel.com editors.", email: "Email", password: "Password", submit: "Sign in", busy: "Signing in…" };

  return (
    <div data-testid="page-admin-login" className="min-h-screen bg-stoel-paper flex items-center justify-center px-6 py-12">
      <form onSubmit={submit} className="w-full max-w-md" autoComplete="on">
        <div className="text-xs uppercase tracking-[0.3em] text-stoel-walnut mb-4">{labels.eyebrow}</div>
        <h1 className="stoel-display text-4xl md:text-5xl mb-3">DeStoel.com</h1>
        <p className="text-stoel-inkSoft mb-10 leading-relaxed">{labels.subtitle}</p>

        <div className="space-y-5">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-stoel-walnut mb-1.5">{labels.email}</span>
            <input
              data-testid="login-email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-stoel-paper border-b border-stoel-ink py-2.5 px-0 text-base focus:outline-none focus:border-stoel-terracotta"
              placeholder="info@destoel.com"
            />
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-stoel-walnut mb-1.5">{labels.password}</span>
            <input
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-stoel-paper border-b border-stoel-ink py-2.5 px-0 text-base focus:outline-none focus:border-stoel-terracotta"
              placeholder="••••••••"
            />
          </label>
        </div>

        {error && (
          <div
            data-testid="login-error"
            className="mt-6 border-l-2 border-stoel-terracotta pl-4 py-2 text-sm text-stoel-ink bg-stoel-paperDeep"
            role="alert"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          data-testid="login-submit"
          disabled={busy || !email || !password}
          className="mt-10 w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 bg-stoel-ink text-stoel-paper hover:bg-stoel-terracotta transition-colors text-sm tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? labels.busy : labels.submit}
        </button>
      </form>
    </div>
  );
}
